"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";
import { Stars, Sparkles } from "lucide-react";
import type { Gift } from "@/types";
import { cn } from "@/lib/utils";
import {
  getExperienceData,
  type StarsMapExperienceData,
} from "@/data/experienceDataSchema";

export type StarsMapVariant = "default" | "spotify";

interface StarsMapPalette {
  accent: string;
  accentRgb: string;
}

interface StarsMapSectionProps {
  gift: Partial<Gift>;
  variant: StarsMapVariant;
  palette?: StarsMapPalette;
  className?: string;
}

interface DecorativeStar {
  cx: number;
  cy: number;
  r: number;
  o: number;
  pulse?: boolean;
}

// Estrelas posicionadas de forma artística (não astronomicamente precisa).
// As 8 primeiras formam a CONSTELAÇÃO DE CORAÇÃO (vértices do desenho),
// as demais são estrelas auxiliares espalhadas pelo céu interno.
const SKY_STARS: DecorativeStar[] = [
  // Coração — vértices (sentido horário, começando no entalhe central de cima)
  { cx: 50, cy: 30, r: 1.4, o: 0.95, pulse: true }, // entalhe topo
  { cx: 62, cy: 22, r: 1.6, o: 1, pulse: true },    // bump direito topo
  { cx: 74, cy: 32, r: 1.2, o: 0.9 },               // direita superior
  { cx: 76, cy: 46, r: 1.4, o: 0.95, pulse: true }, // direita média
  { cx: 50, cy: 74, r: 2.1, o: 1, pulse: true },    // ponta inferior
  { cx: 24, cy: 46, r: 1.4, o: 0.95, pulse: true }, // esquerda média
  { cx: 26, cy: 32, r: 1.2, o: 0.9 },               // esquerda superior
  { cx: 38, cy: 22, r: 1.6, o: 1, pulse: true },    // bump esquerdo topo

  // Estrelas decorativas — espalhadas no fundo
  { cx: 18, cy: 20, r: 0.8, o: 0.55 },
  { cx: 82, cy: 18, r: 0.9, o: 0.6 },
  { cx: 88, cy: 60, r: 0.8, o: 0.5 },
  { cx: 12, cy: 58, r: 0.8, o: 0.5 },
  { cx: 30, cy: 80, r: 1, o: 0.65 },
  { cx: 70, cy: 82, r: 1, o: 0.7 },
  { cx: 50, cy: 14, r: 0.7, o: 0.5 },
  { cx: 16, cy: 78, r: 0.6, o: 0.4 },
  { cx: 86, cy: 78, r: 0.6, o: 0.4 },
];

// Coração desenhado via path SVG conectando os 8 vértices acima com
// curvas de Bezier suaves. Substitui o antigo hexágono — fica visualmente
// "ah, é um coração!" depois de uns segundos olhando.
const HEART_CONSTELLATION_PATH =
  "M 50 30 " +
  "C 54 22 60 18 62 22 " +
  "C 66 18 74 24 74 32 " +
  "C 78 36 78 44 76 46 " +
  "C 72 58 60 70 50 74 " +
  "C 40 70 28 58 24 46 " +
  "C 22 44 22 36 26 32 " +
  "C 26 24 34 18 38 22 " +
  "C 40 18 46 22 50 30 Z";

// Estrela de 5 pontas (icônica) — desenhada na metade superior como
// satélite decorativo do coração principal.
const FIVE_POINT_STAR_PATH =
  "M 85 14 L 86.5 17.5 L 90 18 L 87.4 20.5 L 88.1 24 L 85 22.3 L 81.9 24 L 82.6 20.5 L 80 18 L 83.5 17.5 Z";

// Mini coração satélite — desenho secundário no canto oposto pra reforçar
// a leitura temática mesmo antes do usuário "ver" o coração principal.
const MINI_HEART_PATH =
  "M 15 84 " +
  "C 13 81 10 81 10 84 " +
  "C 10 87 15 90 15 90 " +
  "C 15 90 20 87 20 84 " +
  "C 20 81 17 81 15 84 Z";

// Estrelas pequenas espalhadas FORA do mapa circular para ambientação.
const AMBIENT_STARS: { left: string; top: string; size: number; delay: number }[] = [
  { left: "6%", top: "12%", size: 2, delay: 0 },
  { left: "14%", top: "78%", size: 1.5, delay: 0.6 },
  { left: "88%", top: "10%", size: 2, delay: 1.1 },
  { left: "92%", top: "62%", size: 1.5, delay: 0.3 },
  { left: "48%", top: "5%", size: 1.2, delay: 0.9 },
  { left: "8%", top: "44%", size: 1.2, delay: 1.4 },
  { left: "94%", top: "34%", size: 1, delay: 0.5 },
  { left: "78%", top: "88%", size: 1.4, delay: 0.2 },
  { left: "22%", top: "92%", size: 1.6, delay: 1.6 },
];

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatSpecialDate(date: Date): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function StarsMapSection({
  gift,
  variant,
  palette,
  className,
}: StarsMapSectionProps) {
  const customData = useMemo(
    () => getExperienceData<StarsMapExperienceData>(gift, "stars-map"),
    [gift]
  );
  const effectiveDateRaw =
    customData?.specialDate?.trim() || gift.coupleStartDate;
  const startDate = useMemo(() => parseDate(effectiveDateRaw), [effectiveDateRaw]);
  const formattedDate = startDate ? formatSpecialDate(startDate) : null;
  const customTitle = customData?.title?.trim();
  const customMessage = customData?.message?.trim();
  const customLocation = customData?.locationLabel?.trim();

  if (variant === "spotify") {
    return (
      <SpotifyVariant
        palette={palette ?? { accent: "#1DB954", accentRgb: "29,185,84" }}
        formattedDate={formattedDate}
        className={className}
        title={customTitle}
        message={customMessage}
        locationLabel={customLocation}
      />
    );
  }

  return (
    <DefaultVariant
      formattedDate={formattedDate}
      className={className}
      title={customTitle}
      message={customMessage}
      locationLabel={customLocation}
    />
  );
}

// ─── Variante default — card claro com interior astral premium ─────────────

function DefaultVariant({
  formattedDate,
  className,
  title,
  message,
  locationLabel,
}: {
  formattedDate: string | null;
  className?: string;
  title?: string;
  message?: string;
  locationLabel?: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={cn(
        "stars-map-default relative overflow-hidden rounded-3xl border shadow-sm",
        className
      )}
      style={{
        background:
          "linear-gradient(135deg, #FFE4E6 0%, #FFF7ED 55%, #FFE4F1 100%)",
        borderColor: "rgba(225,29,72,0.18)",
      }}
      aria-label="Mapa das Estrelas — representação artística do céu de um momento especial"
    >
      <div className="relative z-10 flex flex-col gap-6 p-6 sm:p-8">
        <header className="flex flex-col items-center gap-2 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#881337] shadow-sm backdrop-blur">
            <Stars className="h-3 w-3 text-[#E11D48]" />
            Mapa das Estrelas
          </span>
          <h2
            className="text-2xl sm:text-3xl font-semibold leading-tight text-[#1F2937]"
            style={{ fontFamily: "var(--font-serif, serif)" }}
          >
            {title || "O céu do nosso momento especial"}
          </h2>
          <p className="max-w-md text-sm text-[#1F2937]/75 leading-relaxed">
            {message ||
              "Uma representação artística do céu que celebra o início dessa história — feita com carinho, brilho e um pouco de magia."}
          </p>
        </header>

        <div className="flex justify-center">
          <StarMapCanvas
            tone="default"
            outerBackground="radial-gradient(120% 100% at 50% 30%, #1B1346 0%, #0B0820 70%, #06031A 100%)"
            outerBorder="rgba(225,29,72,0.35)"
            ambientStarColor="rgba(255,228,230,0.85)"
            starColor="#FFFFFF"
            starHighlight="#FFD7E2"
            constellationColor="rgba(255,228,230,0.55)"
            glowColor="rgba(255,180,210,0.45)"
            ringColor="rgba(255,255,255,0.18)"
          />
        </div>

        {formattedDate ? (
          <div className="flex flex-col items-center gap-1.5">
            <span className="rounded-full bg-white/70 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#881337] shadow-sm backdrop-blur">
              Desde {formattedDate}
            </span>
            {locationLabel ? (
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#881337]/70">
                {locationLabel}
              </span>
            ) : null}
            <p className="text-xs text-[#1F2937]/60 italic">
              O céu desse dia ficou guardado entre nós.
            </p>
          </div>
        ) : (
          <DefaultEmptyState />
        )}
      </div>
    </motion.section>
  );
}

function DefaultEmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <Sparkles className="h-5 w-5 text-[#E11D48]" />
      <p className="text-sm text-[#1F2937]/75 max-w-xs">
        O céu desse amor ainda guarda uma história só de vocês.
      </p>
    </div>
  );
}

// ─── Variante Spotify — escura, imersiva, integrada à paleta ───────────────

function SpotifyVariant({
  palette,
  formattedDate,
  className,
  title,
  message,
  locationLabel,
}: {
  palette: StarsMapPalette;
  formattedDate: string | null;
  className?: string;
  title?: string;
  message?: string;
  locationLabel?: string;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "stars-map-spotify relative overflow-hidden rounded-2xl",
        className
      )}
      style={{
        background: `radial-gradient(130% 100% at 0% 0%, rgba(${palette.accentRgb},0.18) 0%, rgba(${palette.accentRgb},0.04) 45%, rgba(8,8,12,0.96) 100%)`,
        border: `1px solid rgba(${palette.accentRgb},0.22)`,
      }}
      aria-label="Mapa das Estrelas — representação artística do céu de um momento especial"
    >
      <div className="relative z-10 flex flex-col gap-4 p-4 sm:p-5">
        <header className="flex items-center gap-2">
          <span style={{ color: palette.accent }}>
            <Stars className="h-3.5 w-3.5" />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: palette.accent }}
          >
            Mapa das Estrelas
          </span>
        </header>

        <div className="flex flex-col gap-1">
          <h3 className="text-lg sm:text-xl font-bold text-white leading-tight">
            {title || "O céu do nosso momento especial"}
          </h3>
          <p className="text-[11px] text-white/55 leading-relaxed">
            {message ||
              "Uma representação artística do céu que celebra o início dessa história — feita com carinho."}
          </p>
        </div>

        <div className="flex justify-center">
          <StarMapCanvas
            tone="spotify"
            outerBackground="radial-gradient(120% 100% at 50% 30%, #0F0F1A 0%, #07070F 70%, #050509 100%)"
            outerBorder={`rgba(${palette.accentRgb},0.45)`}
            ambientStarColor={`rgba(${palette.accentRgb},0.85)`}
            starColor="#FFFFFF"
            starHighlight={palette.accent}
            constellationColor={`rgba(${palette.accentRgb},0.55)`}
            glowColor={`rgba(${palette.accentRgb},0.35)`}
            ringColor={`rgba(${palette.accentRgb},0.18)`}
          />
        </div>

        {formattedDate ? (
          <div className="flex flex-col items-center gap-1">
            <span
              className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
              style={{
                color: palette.accent,
                background: `rgba(${palette.accentRgb},0.12)`,
                border: `1px solid rgba(${palette.accentRgb},0.25)`,
              }}
            >
              Desde {formattedDate}
            </span>
            {locationLabel ? (
              <span
                className="text-[10px] uppercase tracking-[0.2em]"
                style={{ color: palette.accent }}
              >
                {locationLabel}
              </span>
            ) : null}
            <p className="text-[11px] text-white/55 italic">
              O céu desse dia ficou guardado entre nós.
            </p>
          </div>
        ) : (
          <SpotifyEmptyState accent={palette.accent} />
        )}
      </div>
    </motion.section>
  );
}

function SpotifyEmptyState({ accent }: { accent: string }) {
  return (
    <div className="flex flex-col items-center gap-1.5 py-1 text-center">
      <Sparkles className="h-4 w-4" style={{ color: accent }} />
      <p className="text-[12px] text-white/70 leading-relaxed max-w-xs">
        O céu desse amor ainda guarda uma história só de vocês.
      </p>
    </div>
  );
}

// ─── Canvas celeste reutilizável ───────────────────────────────────────────

interface StarMapCanvasProps {
  tone: "default" | "spotify";
  outerBackground: string;
  outerBorder: string;
  ambientStarColor: string;
  starColor: string;
  starHighlight: string;
  constellationColor: string;
  glowColor: string;
  ringColor: string;
}

function StarMapCanvas({
  tone,
  outerBackground,
  outerBorder,
  ambientStarColor,
  starColor,
  starHighlight,
  constellationColor,
  glowColor,
  ringColor,
}: StarMapCanvasProps) {
  const size =
    tone === "spotify"
      ? "clamp(180px, 60vw, 240px)"
      : "clamp(220px, 70vw, 320px)";

  return (
    <div
      className="relative flex items-center justify-center"
      style={{
        width: size,
        height: size,
      }}
    >
      <div
        className="absolute inset-0 rounded-full overflow-hidden"
        style={{
          background: outerBackground,
          border: `1px solid ${outerBorder}`,
          boxShadow: `0 18px 50px -20px ${glowColor}, inset 0 0 60px ${glowColor}`,
        }}
      >
        {AMBIENT_STARS.map((star, i) => (
          <span
            key={i}
            className="stars-map-ambient absolute rounded-full"
            style={{
              left: star.left,
              top: star.top,
              width: `${star.size}px`,
              height: `${star.size}px`,
              background: ambientStarColor,
              opacity: 0.7,
              animationDelay: `${star.delay}s`,
            }}
          />
        ))}

        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id={`star-glow-${tone}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={starHighlight} stopOpacity="0.45" />
              <stop offset="100%" stopColor={starHighlight} stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle
            cx="50"
            cy="50"
            r="49"
            fill="none"
            stroke={ringColor}
            strokeWidth="0.4"
          />
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke={ringColor}
            strokeOpacity="0.6"
            strokeWidth="0.25"
            strokeDasharray="0.6 1.4"
          />

          <circle cx="50" cy="46" r="36" fill={`url(#star-glow-${tone})`} />

          {/* Coração principal — silhueta tracejada conectando as estrelas */}
          <path
            d={HEART_CONSTELLATION_PATH}
            fill="none"
            stroke={constellationColor}
            strokeWidth="0.45"
            strokeDasharray="0.9 1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stars-map-constellation"
          />

          {/* Estrela de 5 pontas — satélite decorativo no canto */}
          <path
            d={FIVE_POINT_STAR_PATH}
            fill={starHighlight}
            opacity="0.7"
            className="stars-map-pulse"
            style={{ transformOrigin: "85px 19px" }}
          />

          {/* Mini coração — reforça o tema no canto inferior esquerdo */}
          <path
            d={MINI_HEART_PATH}
            fill={starHighlight}
            opacity="0.55"
            className="stars-map-pulse"
            style={{ transformOrigin: "15px 86px" }}
          />

          {SKY_STARS.map((star, i) => (
            <circle
              key={i}
              cx={star.cx}
              cy={star.cy}
              r={star.r}
              fill={star.pulse ? starHighlight : starColor}
              opacity={star.o}
              className={star.pulse ? "stars-map-pulse" : undefined}
              style={
                star.pulse
                  ? { transformOrigin: `${star.cx}px ${star.cy}px` }
                  : undefined
              }
            />
          ))}
        </svg>
      </div>

      <style jsx>{`
        .stars-map-ambient {
          animation: stars-map-twinkle 3.6s ease-in-out infinite;
        }

        @keyframes stars-map-twinkle {
          0%,
          100% {
            opacity: 0.25;
            transform: scale(0.9);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.1);
          }
        }

        :global(.stars-map-pulse) {
          animation: stars-map-star-pulse 2.8s ease-in-out infinite;
        }

        :global(.stars-map-constellation) {
          animation: stars-map-constellation-glow 6s ease-in-out infinite;
        }

        @keyframes stars-map-constellation-glow {
          0%,
          100% {
            opacity: 0.55;
          }
          50% {
            opacity: 0.95;
          }
        }

        @keyframes stars-map-star-pulse {
          0%,
          100% {
            opacity: 0.55;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.35);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .stars-map-ambient,
          :global(.stars-map-pulse) {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
